import React, { useState } from "react";

export default function Contact({ t }) {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      if (!backendUrl) {
        console.error("REACT_APP_BACKEND_URL is not defined");
        setStatus({ type: "error", message: "Backend URL is not configured. Please contact support." });
        setIsSubmitting(false);
        return;
      }

      const response = await fetch(backendUrl + "/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setStatus({ type: "success", message: t.contact_success || "Το μήνυμά σας στάλθηκε επιτυχώς!" });
        setFormData({ name: "", email: "", message: "" });
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("Server error:", response.status, errorData);
        setStatus({ type: "error", message: t.contact_error || "Σφάλμα κατά την αποστολή. Παρακαλώ προσπαθήστε ξανά." });
      }
    } catch (error) {
      console.error("Contact form error:", error);
      setStatus({ type: "error", message: t.contact_error || "Σφάλμα κατά την αποστολή. Παρακαλώ προσπαθήστε ξανά." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      style={{
        padding: "6rem 2rem",
        background: "var(--bg-secondary)",
        position: "relative",
        overflow: "hidden"
      }}
    >
      {/* glow */}
      <div style={{
        position: "absolute", top: "20%", left: "5%",
        width: "350px", height: "350px",
        background: "radial-gradient(circle, rgba(99,102,241,0.1) 0%, transparent 70%)",
        pointerEvents: "none"
      }} />

      <div style={{ maxWidth: "680px", margin: "0 auto", position: "relative" }}>
        <p style={{
          textAlign: "center",
          fontSize: "0.75rem",
          fontWeight: "600",
          letterSpacing: "3px",
          textTransform: "uppercase",
          color: "var(--accent-purple)",
          marginBottom: "0.8rem"
        }}>
          {t.contact}
        </p>

        <h2 style={{
          textAlign: "center",
          fontSize: "2.2rem",
          fontWeight: "800",
          color: "var(--text-primary)",
          marginBottom: "0.8rem",
          letterSpacing: "-0.03em"
        }}>
          {t.contact_title}
        </h2>

        <p style={{
          textAlign: "center",
          color: "var(--text-secondary)",
          marginBottom: "2.5rem",
          lineHeight: "1.7"
        }}>
          {t.contact_description}
        </p>

        <form onSubmit={handleSubmit} style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border)",
          borderRadius: "20px",
          padding: "2.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "1.4rem"
        }}>
          <div>
            <label htmlFor="name" style={labelStyle}>
              {t.contact_name} <span style={{ color: "#f87171" }}>*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder={t.contact_name}
              style={inputStyle}
            />
          </div>

          <div>
            <label htmlFor="email" style={labelStyle}>
              Email <span style={{ color: "#f87171" }}>*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="email@company.com"
              style={inputStyle}
            />
          </div>

          <div>
            <label htmlFor="message" style={labelStyle}>
              {t.contact_message} <span style={{ color: "#f87171" }}>*</span>
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows="5"
              placeholder="..."
              style={{ ...inputStyle, resize: "vertical", minHeight: "120px" }}
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              padding: "0.9rem 2rem",
              fontSize: "1rem",
              fontWeight: "700",
              background: isSubmitting
                ? "rgba(139,92,246,0.3)"
                : "linear-gradient(135deg, #6366f1, #8b5cf6)",
              color: "#fff",
              border: "none",
              borderRadius: "12px",
              cursor: isSubmitting ? "not-allowed" : "pointer",
              transition: "transform 0.2s, box-shadow 0.2s, filter 0.2s",
              boxShadow: "0 4px 20px rgba(139,92,246,0.4)",
              letterSpacing: "-0.01em"
            }}
            onMouseEnter={e => { if (!isSubmitting) { e.target.style.transform = "translateY(-2px)"; e.target.style.filter = "brightness(1.1)"; } }}
            onMouseLeave={e => { e.target.style.transform = "none"; e.target.style.filter = "none"; }}
          >
            {isSubmitting ? t.contact_sending : t.contact_send}
          </button>

          {status.message && (
            <div style={{
              padding: "1rem 1.2rem",
              borderRadius: "12px",
              background: status.type === "success"
                ? "rgba(16,217,168,0.1)"
                : "rgba(248,113,113,0.1)",
              border: `1px solid ${status.type === "success" ? "rgba(16,217,168,0.3)" : "rgba(248,113,113,0.3)"}`,
              color: status.type === "success" ? "#10d9a8" : "#f87171",
              fontSize: "0.9rem"
            }}>
              {status.message}
            </div>
          )}
        </form>
      </div>
    </section>
  );
}

const labelStyle = {
  display: "block",
  marginBottom: "0.5rem",
  fontSize: "0.85rem",
  fontWeight: "600",
  color: "var(--text-secondary)",
  letterSpacing: "0.01em"
};

const inputStyle = {
  width: "100%",
  padding: "0.8rem 1rem",
  fontSize: "0.95rem",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid var(--border)",
  borderRadius: "10px",
  boxSizing: "border-box",
  color: "var(--text-primary)",
  transition: "border-color 0.2s, box-shadow 0.2s",
  fontFamily: "inherit"
};
